<template>
	<div
		class="four-slot-offer-card-detail template-layout premium-shop-item__content"
	>
		<div class="premium-shop-item__values">
			<ScrollFade :direction="ScrollDirection.HORIZONTAL">
				<template #scrollContent>
					<div class="premium-shop-item__rewards">
						<div v-if="firstSlotItems">
							<BuildingRewardsRow
								v-if="
									firstSlotGroup === CommodityGroup.BUILDING
								"
								:items="firstSlotItems as BuildingSlotItem[]"
								:context="RewardsGridContext.CARD"
								:template="contentData.attributes.template"
							/>
							<UpgradeRewardsRow
								v-if="firstSlotGroup === CommodityGroup.UPGRADE"
								:items="firstSlotItems as UpgradeSlotItem[]"
								:context="RewardsGridContext.CARD"
								:template="contentData.attributes.template"
							/>
						</div>
						<span class="premium-shop-item__plus-divider">+</span>
						<div class="complementary-row-container">
							<div v-if="secondSlotItems">
								<UpgradeRewardsRow
									v-if="
										secondSlotGroup ===
										CommodityGroup.UPGRADE
									"
									:items="
										secondSlotItems as UpgradeSlotItem[]
									"
									:context="RewardsGridContext.CARD"
									:template="contentData.attributes.template"
								/>
								<ResourceOrUtilityRewardsRow
									v-if="
										secondSlotGroup ===
										CommodityGroup.RESOURCE
									"
									:items="
										secondSlotItems as ResourceOrUtilitySlotItem[]
									"
									:context="RewardsGridContext.CARD"
								/>
							</div>

							<div v-if="thirdSlotItems">
								<ResourceOrUtilityRewardsRow
									v-if="
										thirdSlotGroup ===
											CommodityGroup.RESOURCE ||
										thirdSlotGroup ===
											CommodityGroup.UTILITY
									"
									:items="
										thirdSlotItems as ResourceOrUtilitySlotItem[]
									"
									:context="RewardsGridContext.CARD"
								/>
							</div>

							<div v-if="fourthSlotItems">
								<ResourceOrUtilityRewardsRow
									v-if="
										fourthSlotGroup ===
										CommodityGroup.UTILITY
									"
									:items="
										fourthSlotItems as ResourceOrUtilitySlotItem[]
									"
									:context="RewardsGridContext.CARD"
								/>
								<EventResourceRewardsRow
									v-if="
										fourthSlotGroup ===
										CommodityGroup.EVENT_RESOURCE
									"
									:items="
										fourthSlotItems as EventResourceSlotItem[]
									"
									:context="RewardsGridContext.CARD"
								/>
							</div>
						</div>
					</div>
				</template>
			</ScrollFade>
		</div>
	</div>
</template>

<script lang="ts" setup>
import ScrollFade from "@/components/ScrollFade.vue";
import BuildingRewardsRow from "@/components/shop/blocks/parts/BuildingRewardsRow.vue";
import EventResourceRewardsRow from "@/components/shop/blocks/parts/EventResourceRewardsRow.vue";
import ResourceOrUtilityRewardsRow from "@/components/shop/blocks/parts/ResourceOrUtilityRewardsRow.vue";
import UpgradeRewardsRow from "@/components/shop/blocks/parts/UpgradeRewardsRow.vue";
import type {
	BuildingSlotItem,
	EventResourceSlotItem,
	ResourceOrUtilitySlotItem,
	UpgradeSlotItem,
} from "@/types/shop";
import { CommodityGroup, type ShopItem } from "@/types/shop";
import { RewardsGridContext, ScrollDirection } from "@/types/ui";
import { computed } from "vue";

interface Props {
	contentData: ShopItem;
}

const { contentData } = defineProps<Props>();

const firstSlotGroup = computed((): CommodityGroup | null => {
	const firstSlot = contentData.attributes?.slots?.[0];
	const firstItem = firstSlot?.items?.[0];
	return firstItem?.group ?? null;
});

const secondSlotGroup = computed((): CommodityGroup | null => {
	const secondSlot = contentData.attributes?.slots?.[1];
	const secondItem = secondSlot?.items?.[0];
	return secondItem?.group ?? null;
});

const thirdSlotGroup = computed((): CommodityGroup | null => {
	const thirdSlot = contentData.attributes?.slots?.[2];
	const thirdItem = thirdSlot?.items?.[0];
	return thirdItem?.group ?? null;
});

const fourthSlotGroup = computed((): CommodityGroup | null => {
	const fourthSlot = contentData.attributes?.slots?.[3];
	const fourthItem = fourthSlot?.items?.[0];
	return fourthItem?.group ?? null;
});

const firstSlotItems = computed((): BuildingSlotItem[] | UpgradeSlotItem[] => {
	return (
		(contentData.attributes?.slots?.[0]?.items as
			| BuildingSlotItem[]
			| UpgradeSlotItem[]) ?? null
	);
});

const secondSlotItems = computed(
	(): UpgradeSlotItem[] | ResourceOrUtilitySlotItem[] => {
		return (
			(contentData.attributes?.slots?.[1]?.items as
				| UpgradeSlotItem[]
				| ResourceOrUtilitySlotItem[]) ?? null
		);
	},
);

const thirdSlotItems = computed((): ResourceOrUtilitySlotItem[] => {
	return (
		(contentData.attributes?.slots?.[2]
			?.items as ResourceOrUtilitySlotItem[]) ?? null
	);
});

const fourthSlotItems = computed(
	(): ResourceOrUtilitySlotItem[] | EventResourceSlotItem[] => {
		return (
			(contentData.attributes?.slots?.[3]?.items as
				| ResourceOrUtilitySlotItem[]
				| EventResourceSlotItem[]) ?? null
		);
	},
);
</script>

<style lang="scss" scoped>
.premium-shop-item__content {
	position: relative;
	flex: 1 1 auto;
	width: 100%;
	min-height: 0;
	@include flex-row-center;
	height: 100%;

	.premium-shop-item {
		&__values {
			flex: 1 1 auto;
			@include flex-row-center;
			justify-content: flex-start;
			padding-bottom: 2.5rem;
			height: 100%;
			overflow: hidden;

			.scroll-fade-container {
				display: flex;

				:deep(.scrollbar__wrapper) {
					height: 100%;

					.scrollbar__scroller {
						height: 100%;

						.scrollbar__content.scrollbar__content--horizontal {
							width: 100%;
							height: 100%;
						}
					}
				}
			}
		}

		&__plus-divider {
			color: #f4d278;
			font-weight: 800;
			font-size: 2.5rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}

		&__rewards {
			@include flex-row-center;
			flex: 1 1 auto;
			gap: 0rem; // SLIM fix

			.complementary-row-container {
				@include flex-row-center;
				gap: 0.125rem; // SLIM fix
			}
		}
	}
}
</style>
